import { equipCollection } from '@/core/equip'
import { equipAffixCollection } from '@/core/equip/affix'
import { getSupremeRealAffixList, supremeAffixCollection } from '@/core/equip/supreme'
import type { DarkGoldAttrType, EngraveAttrType, EquipItemType, EquipType } from '@/types/equip'
import type { ItemType } from '@/types/item'
import { equipIcon, equipTypeList, supremeAffixLevelQualityMap } from '@/views/item/data'
import lockIconUrl from '@/assets/icons/lock.svg'

const starStr: Record<number, string> = {
  0: '',
  1: '<b style="color: #fff">☆</b> ',
  2: '<b style="color: #fff">★</b> ',
  3: '<b style="color: #abced8">★</b> ',
  4: '<b style="color: #eacd76">★</b> ',
  5: '<b style="color: #884898">★</b> ',
}
const overLimitStarStr: Record<number, string> = {
  0: '',
  1: '<b style="color: #abced8">★</b> ',
  2: '<b style="color: #3e62ed">★</b> ',
  3: '<b style="color: #8d4bbb">★</b> ',
  4: '<b style="color: #d66a35">★</b> ',
  5: '<b style="color: #f05654">★</b> ',
}

export const markColorMap: Record<number, string> = {
  0: '#fff',
  1: '#f05654',
  2: '#d66a35',
  3: '#eacd76',
  4: '#67c23a',
  5: '#abced8',
  6: '#3e62ed',
  7: '#8d4bbb',
}

function getEnchantStr(enchant: [number, number] | undefined) {
  if (!enchant) return ''
  let res = '<span class="enchant-wrap"><span class="enchant">'
  const enchantLevel = enchant[0]
  for (let i = 0; i < enchantLevel; i++) {
    res += '<span></span>'
  }
  res += '</span></span>'
  return res
}
function getRefineStr(refineLevel?: number, isSupreme = false) {
  if (!refineLevel) return ''
  let res = `<span class="${isSupreme ? 'supreme-refine ' : ''}refine-wrap"><span class="refine">`
  let fullCount = refineLevel % 5
  if (refineLevel > 0 && !fullCount) fullCount = 5
  for (let i = 0; i < fullCount; i++) {
    res += `<span>${getIcon(refineLevel, true)}</span>`
  }
  for (let i = 0; i < 5 - fullCount; i++) {
    res += `<span>${getIcon(refineLevel, false)}</span>`
  }
  function getIcon(level: number, isFull: boolean) {
    if (isFull) {
      if (level <= 5) {
        return '◆'
      } else if (level <= 10) {
        return '■'
      } else {
        return '★'
      }
    } else {
      if (level <= 5) {
        return '◇'
      } else if (level <= 10) {
        return '□'
      } else {
        return '☆'
      }
    }
  }

  res += '</span></span>'
  return res
}

export function getEquipName(item: EquipItemType | undefined, iconIndex?: number | EquipType, showLockIcon = true) {
  let icon = ''
  if (typeof iconIndex === 'number') icon = equipIcon[equipTypeList.value[iconIndex]] + ' '
  else if (typeof iconIndex === 'string') icon = equipIcon[iconIndex] + ' '

  if (!item) return `${icon} (未装备)`
  const reinforcedLevel = item.reinforcedLevel ? ` +${item.reinforcedLevel}` : ''
  const star = starStr[item.starAttrs?.length || 0]
  const enchant = getEnchantStr(item.enchantAttr)
  const refine = getRefineStr(item.refineAttr?.[0], item.quality === 'supreme')
  let overLimitIcon = ''
  if (item.quality === 'darkGold') {
    overLimitIcon = getDarkGoldOverLimitIcon(item.darkGoldAttrs, item.engraveAttr)
  } else if (item.quality === 'myth') {
    overLimitIcon = getDarkGoldOverLimitIcon(item.darkGoldAttrs, item.engraveAttr, true)
  } else if (item.quality === 'supreme') {
    overLimitIcon = overLimitStarStr[(item.supremeAttrs?.level || 1) - 1]
  }
  let darkGoldSpecName = ''
  if (['darkGold', 'myth'].includes(item.quality)) {
    const specArr: string[] = []
    if (item.darkGoldAttrs?.special) {
      specArr.push(...item.darkGoldAttrs?.special.map((item) => equipAffixCollection[item.key].name))
    }
    if (item.engraveAttr?.special) {
      specArr.push(...item.engraveAttr?.special.map((item) => equipAffixCollection[item.key].name))
    }
    if (specArr.length) {
      darkGoldSpecName = `<span class="dark-gold-spec-info">[${specArr.join('] [')}]</span>`
    }
  }
  if (item.supremeAttrs) {
    const affixList = getSupremeRealAffixList(item.supremeAttrs, item.supremeEngraveAttr, item.supremeReforgeAttr)
    darkGoldSpecName = `<span class="dark-gold-spec-info">[${affixList
      .map(
        (key) =>
          `<span style="display: inline" class="${supremeAffixLevelQualityMap[supremeAffixCollection[key].level]} supreme-affix">${
            supremeAffixCollection[key].name
          }</span>`,
      )
      .join('] [')}]</span>`
  }
  const lockIcon =
    showLockIcon && item.isLock
      ? `<img src="${lockIconUrl}" style="position: absolute; top: 0; left: 0; z-index: 1; width: 1em; height: 1em;" aria-hidden="true" />`
      : ''
  const markIcon =
    showLockIcon && item.markType && markColorMap[item.markType]
      ? `<span style="position: absolute; top: 0; right: 0; z-index: 1; width: 0.8em; height: 0.8em; border-radius: 50%; border: 0.5px #fff solid; background: ${markColorMap[item.markType]};" aria-hidden="true"></span>`
      : ''

  return `${icon}${star}${overLimitIcon}<label class="${item.quality}">${item.name}${reinforcedLevel}</label>${enchant}${refine}${darkGoldSpecName}${lockIcon}${markIcon}`
}

export function getEquipInfoList(itemList: ItemType[]) {
  return itemList.map((item) => {
    const { equipId } = item
    const { name, type, attrs } = equipCollection[equipId]
    return { ...item, name, type, attrs }
  })
}

function getDarkGoldOverLimitIcon(darkGoldAttrs?: DarkGoldAttrType, engraveAttr?: EngraveAttrType, isMyth?: boolean) {
  let overLimit = 0
  darkGoldAttrs?.basic.forEach(([_key, val]) => {
    if (val === 10) overLimit++
  })
  darkGoldAttrs?.special?.forEach(({ data }) => {
    if (data.value === 10 || data.multiplier === 10 || data.rate === 10) overLimit++
  })
  engraveAttr?.special.forEach(({ data }) => {
    if (data.value === 10 || data.multiplier === 10 || data.rate === 10) overLimit++
  })
  if (isMyth && overLimit > 0) overLimit++

  return overLimitStarStr[overLimit]
}
